import React, { useContext, useEffect, useState } from "react";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { Share2 } from "lucide-react";
import { NetworkMemberType } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { networkModes } from "components/Environment/Networks/networkMode";
import { formatDistanceToNow } from "date-fns";
import { UserContext } from "contexts/UserContext";
import {
  RICH_TOOLTIP_CONTENT_CLASS_NAME,
  TOOLTIP_ARROW_CLASS_NAME,
} from "components/Environment/shared/DisabledActionTooltip";
import LoadingText from "components/Graphs/LoadingText";

const fullTimestampFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  month: "short",
  second: "2-digit",
  timeZoneName: "short",
  year: "numeric",
});

export const ComputeNetworkMode: React.FC<{
  memberType?: NetworkMemberType | null;
  loading?: boolean;
}> = ({ memberType, loading = false }) => {
  const isMeshed = memberType === NetworkMemberType.SHARED;
  const mode = networkModes.find(({ type }) => type === memberType);
  const label = loading
    ? "LOADING"
    : memberType === undefined
      ? "NO MESH"
      : (mode?.label.toUpperCase() ?? "PENDING");
  const isMeshedResolved = !loading && isMeshed;
  const tooltipMessage = loading
    ? undefined
    : memberType === undefined
      ? "This node is not connected to the organization's mesh network."
      : (mode?.description ?? "This node's network mode is pending.");

  const networkMode = (
    <span
      tabIndex={tooltipMessage ? 0 : undefined}
      onClick={(event) => event.stopPropagation()}
      className={`inline-flex h-5 w-[7rem] shrink-0 items-center gap-1.5 leading-none ${loading ? "justify-center" : "pl-1"} ${isMeshedResolved ? "font-bold tracking-[0.75px] text-white" : `${loading ? "font-normal" : "font-semibold"} text-[#9f9e9e]`}`}
    >
      <Share2
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0"
        strokeWidth={isMeshedResolved ? 2.5 : 1.75}
      />
      {loading ? <LoadingText text={label} /> : label}
    </span>
  );

  if (!tooltipMessage) return networkMode;

  return (
    <TooltipPrimitives.Provider delayDuration={150} skipDelayDuration={100}>
      <TooltipPrimitives.Root>
        <TooltipPrimitives.Trigger asChild>
          {networkMode}
        </TooltipPrimitives.Trigger>
        <TooltipPrimitives.Portal>
          <TooltipPrimitives.Content
            side="top"
            align="start"
            sideOffset={8}
            collisionPadding={8}
            className={RICH_TOOLTIP_CONTENT_CLASS_NAME}
          >
            {tooltipMessage}
            <TooltipPrimitives.Arrow className={TOOLTIP_ARROW_CLASS_NAME} />
          </TooltipPrimitives.Content>
        </TooltipPrimitives.Portal>
      </TooltipPrimitives.Root>
    </TooltipPrimitives.Provider>
  );
};

export const ComputeCardKindLabel: React.FC<{ kind: "cloud" | "local" }> = ({
  kind,
}) => (
  <span
    className={`inline-flex h-5 shrink-0 items-center pr-2 text-xs font-black uppercase leading-none tracking-normal ${
      kind === "cloud" ? "text-secondary" : "text-[#dfc597]"
    }`}
  >
    {kind === "cloud" ? "CLOUD" : "LOCAL"}
  </span>
);

export const ComputeCardRelativeTime: React.FC<{ date: Date }> = ({ date }) => {
  const fullTimestamp = fullTimestampFormatter.format(date);

  return (
    <time
      className="inline-flex h-5 items-center leading-none"
      dateTime={date.toISOString()}
      title={fullTimestamp}
    >
      {formatDistanceToNow(date)} ago
    </time>
  );
};

export const ComputeCardOwner: React.FC<{ userId?: string }> = ({ userId }) => {
  const { getUser } = useContext(UserContext);
  const [resolvedUser, setResolvedUser] = useState<{
    userId?: string;
    username: string | null;
  }>({ userId: undefined, username: null });

  useEffect(() => {
    let cancelled = false;

    if (!userId) return;

    void getUser(userId).then((user) => {
      if (cancelled) return;
      setResolvedUser({ userId, username: user?.username || null });
    });

    return () => {
      cancelled = true;
    };
  }, [getUser, userId]);

  const loading = Boolean(userId && resolvedUser.userId !== userId);
  const username =
    userId && resolvedUser.userId === userId ? resolvedUser.username : null;

  return (
    <span className="inline-block max-w-[12rem] truncate text-sm leading-none text-secondary">
      {userId ? (loading ? "Loading" : username || "Unknown") : "Unknown"}
    </span>
  );
};
